import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, User, BookOpen, Calendar, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const StudentEnrollmentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTutorId = searchParams.get('tutor');

  const [tutors, setTutors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    // Student/Parent Details
    studentFullName: '',
    parentGuardianName: '',
    email: '',
    phone: '',
    suburb: '',
    city: '',
    state: '',
    
    // Academic Details
    programType: 'VCE',
    subject: '',
    yearLevel: '',
    currentChallenges: '',
    goals: '',
    
    // Preferences
    preferredFormat: 'Both',
    preferredDaysTimes: '',
    additionalNotes: '',
    
    // Tutor Selection
    tutorSelectionType: preselectedTutorId ? 'specific' : 'match_me',
    requestedTutorId: preselectedTutorId || ''
  });

  useEffect(() => {
    fetchTutors();
  }, []);

  const fetchTutors = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/tutors`);
      const data = await response.json();
      if (data.success) {
        setTutors(data.tutors || []);
      }
    } catch (error) {
      // Log error in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch tutors:', error);
      }
      // Use empty array as fallback - user experience not impacted
      setTutors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.studentFullName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required student details');
      setCurrentStep(1);
      return;
    }

    if (!formData.subject || !formData.yearLevel || !formData.goals) {
      toast.error('Please complete academic details');
      setCurrentStep(2);
      return;
    }

    if (!formData.preferredDaysTimes) {
      toast.error('Please provide your availability preferences');
      setCurrentStep(3);
      return;
    }

    if (formData.tutorSelectionType === 'specific' && !formData.requestedTutorId) {
      toast.error('Please select a tutor');
      setCurrentStep(4);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/student-enrollments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Submission failed');

      const data = await response.json();
      
      toast.success('Enrollment submitted successfully!');
      
      // Redirect to success page with enrollment ID
      setTimeout(() => {
        navigate('/enrollment-success', { 
          state: { enrollmentId: data.enrollment?.id } 
        });
      }, 1500);

    } catch (error) {
      toast.error('Failed to submit enrollment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getTutorName = (tutorId) => {
    const tutor = tutors.find(t => t.id === tutorId);
    return tutor ? tutor.fullName : '';
  };

  const steps = [
    { num: 1, title: 'Student Details', icon: User },
    { num: 2, title: 'Academic Info', icon: BookOpen },
    { num: 3, title: 'Availability', icon: Calendar },
    { num: 4, title: 'Choose Tutor', icon: Target }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="premium-gradient text-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-xl mb-4">Get Started with Winhouse Tutors</h1>
          <p className="text-lg opacity-95">
            Begin your journey to academic excellence. Complete the form below and we'll match you with the perfect tutor.
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-8">
        <Card className="premium-card p-4">
          <div className="flex justify-between items-center">
            {steps.map((step, idx) => (
              <React.Fragment key={step.num}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    currentStep >= step.num 
                      ? 'text-white' 
                      : 'text-gray-400'
                  }`}
                  style={{ 
                    backgroundColor: currentStep >= step.num ? 'var(--accent-teal)' : 'var(--bg-light)'
                  }}>
                    {currentStep > step.num ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs md:text-sm ${
                    currentStep >= step.num ? 'font-medium' : ''
                  }`}
                  style={{ color: currentStep >= step.num ? 'var(--text-primary)' : 'var(--text-grey)' }}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2" style={{ 
                    backgroundColor: currentStep > step.num ? 'var(--accent-teal)' : 'var(--border-color)'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </Card>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <form onSubmit={handleSubmit}>
          {/* Step 1: Student Details */}
          {currentStep === 1 && (
            <Card className="premium-card p-6 md:p-8 mb-8">
              <h2 className="heading-md mb-6">Student & Parent Details</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="form-label">Student Full Name *</Label>
                  <Input
                    required
                    value={formData.studentFullName}
                    onChange={(e) => handleChange('studentFullName', e.target.value)}
                    placeholder="Enter student's full name"
                  />
                </div>

                <div>
                  <Label className="form-label">Parent/Guardian Name (Optional)</Label>
                  <Input
                    value={formData.parentGuardianName}
                    onChange={(e) => handleChange('parentGuardianName', e.target.value)}
                    placeholder="Enter parent/guardian name"
                  />
                </div>

                <div>
                  <Label className="form-label">Email Address *</Label>
                  <Input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label className="form-label">Phone Number *</Label>
                  <Input
                    required
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="04XX XXX XXX"
                  />
                </div>

                <div>
                  <Label className="form-label">Suburb</Label>
                  <Input
                    value={formData.suburb}
                    onChange={(e) => handleChange('suburb', e.target.value)}
                    placeholder="Enter suburb"
                  />
                </div>

                <div>
                  <Label className="form-label">City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Melbourne"
                  />
                </div>

                <div>
                  <Label className="form-label">State</Label>
                  <select
                    className="w-full px-4 py-2 rounded-md border"
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    style={{ borderColor: 'var(--border-color)' }}
                  >
                    <option value="">Select state</option>
                    <option value="VIC">Victoria</option>
                    <option value="NSW">New South Wales</option>
                    <option value="QLD">Queensland</option>
                    <option value="SA">South Australia</option>
                    <option value="WA">Western Australia</option>
                    <option value="TAS">Tasmania</option>
                    <option value="NT">Northern Territory</option>
                    <option value="ACT">Australian Capital Territory</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="premium-button"
                >
                  Next: Academic Details
                </Button>
              </div>
            </Card>
          )}

          {/* Step 2: Academic Details */}
          {currentStep === 2 && (
            <Card className="premium-card p-6 md:p-8 mb-8">
              <h2 className="heading-md mb-6">Academic Details</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="form-label">Program Type *</Label>
                  <div className="grid md:grid-cols-3 gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => handleChange('programType', 'VCE')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.programType === 'VCE' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-1">VCE Tutoring</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Year 10, 11, 12 / VCE subjects
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('programType', 'IELTS')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.programType === 'IELTS' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-1">IELTS Coaching</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        IELTS preparation and band improvement
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('programType', 'GMAT')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.programType === 'GMAT' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-1">GMAT</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Graduate Management Admission Test
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('programType', 'GRE')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.programType === 'GRE' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-1">GRE</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Graduate Record Examination
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('programType', 'SAT')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.programType === 'SAT' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-1">SAT</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Scholastic Assessment Test
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('programType', 'PTE')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.programType === 'PTE' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-1">PTE</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Pearson Test of English Academic
                      </p>
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="form-label">Subject / Focus Area *</Label>
                    <Input
                      required
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      placeholder={formData.programType === 'VCE' ? 'e.g., English, Maths Methods' : 'e.g., Writing, Speaking'}
                    />
                  </div>

                  <div>
                    <Label className="form-label">Year Level / Current Band *</Label>
                    <Input
                      required
                      value={formData.yearLevel}
                      onChange={(e) => handleChange('yearLevel', e.target.value)}
                      placeholder={formData.programType === 'VCE' ? 'e.g., Year 12' : 'e.g., Band 6'}
                    />
                  </div>
                </div>

                <div>
                  <Label className="form-label">Current Challenges *</Label>
                  <Textarea
                    required
                    value={formData.currentChallenges}
                    onChange={(e) => handleChange('currentChallenges', e.target.value)}
                    placeholder="What areas are you finding challenging? Be specific..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="form-label">Goals & Target Outcomes *</Label>
                  <Textarea
                    required
                    value={formData.goals}
                    onChange={(e) => handleChange('goals', e.target.value)}
                    placeholder="What do you want to achieve? (e.g., improve from 35 to 40+ in VCE English, achieve Band 7 in IELTS)"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <Button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="premium-button-secondary"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="premium-button"
                >
                  Next: Availability
                </Button>
              </div>
            </Card>
          )}

          {/* Step 3: Availability */}
          {currentStep === 3 && (
            <Card className="premium-card p-6 md:p-8 mb-8">
              <h2 className="heading-md mb-6">Preferences & Availability</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="form-label">Preferred Lesson Format *</Label>
                  <div className="grid md:grid-cols-3 gap-4 mt-2">
                    {['Online', 'In-Person', 'Both'].map(format => (
                      <button
                        key={format}
                        type="button"
                        onClick={() => handleChange('preferredFormat', format)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          formData.preferredFormat === format 
                            ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                            : 'border-[var(--border-color)]'
                        }`}
                      >
                        <span className="font-medium">{format}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="form-label">Preferred Days & Times *</Label>
                  <Textarea
                    required
                    value={formData.preferredDaysTimes}
                    onChange={(e) => handleChange('preferredDaysTimes', e.target.value)}
                    placeholder="e.g., Monday and Wednesday 4-6pm, Saturday mornings"
                    rows={3}
                  />
                </div>

                <div>
                  <Label className="form-label">Additional Notes (Optional)</Label>
                  <Textarea
                    value={formData.additionalNotes}
                    onChange={(e) => handleChange('additionalNotes', e.target.value)}
                    placeholder="Any other information or specific requirements..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <Button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="premium-button-secondary"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => setCurrentStep(4)}
                  className="premium-button"
                >
                  Next: Choose Tutor
                </Button>
              </div>
            </Card>
          )}

          {/* Step 4: Tutor Selection */}
          {currentStep === 4 && (
            <Card className="premium-card p-6 md:p-8 mb-8">
              <h2 className="heading-md mb-6">Choose Your Tutor</h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="form-label">How would you like to proceed?</Label>
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => handleChange('tutorSelectionType', 'match_me')}
                      className={`p-6 rounded-lg border-2 transition-all ${
                        formData.tutorSelectionType === 'match_me' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-2">Match Me With the Best Tutor</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Our team will review your needs and match you with the perfect tutor
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('tutorSelectionType', 'specific')}
                      className={`p-6 rounded-lg border-2 transition-all ${
                        formData.tutorSelectionType === 'specific' 
                          ? 'border-[var(--accent-teal)] bg-[var(--accent-teal)]/5' 
                          : 'border-[var(--border-color)]'
                      }`}
                    >
                      <h3 className="font-semibold mb-2">I Want to Choose a Specific Tutor</h3>
                      <p className="text-sm" style={{ color: 'var(--text-grey)' }}>
                        Select from our available tutors below
                      </p>
                    </button>
                  </div>
                </div>

                {formData.tutorSelectionType === 'specific' && (
                  <div>
                    <Label className="form-label">Select Tutor *</Label>
                    <select
                      className="w-full px-4 py-2 rounded-md border"
                      value={formData.requestedTutorId}
                      onChange={(e) => handleChange('requestedTutorId', e.target.value)}
                      style={{ borderColor: 'var(--border-color)' }}
                      required={formData.tutorSelectionType === 'specific'}
                    >
                      <option value="">Select a tutor...</option>
                      {tutors.map(tutor => (
                        <option key={tutor.id} value={tutor.id}>
                          {tutor.fullName} - {tutor.subjects?.slice(0, 2).join(', ')}
                        </option>
                      ))}
                    </select>
                    {formData.requestedTutorId && (
                      <p className="text-sm mt-2" style={{ color: 'var(--text-grey)' }}>
                        You've selected: <strong>{getTutorName(formData.requestedTutorId)}</strong>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-between">
                <Button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="premium-button-secondary"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="premium-button"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Enrollment'}
                </Button>
              </div>
            </Card>
          )}
        </form>
      </div>
    </div>
  );
};

export default StudentEnrollmentPage;
